import { ToastrService } from 'ngx-toastr';
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ViewUpdateTprApiService } from '../../../../../../modules/api/view-update-tpr-api.service';
import { ViewUpdateTpr } from '../../view-update-tpr.class';
import { MatLegacyCheckboxChange as MatCheckboxChange, MatLegacyCheckbox as MatCheckbox } from '@angular/material/legacy-checkbox';
import { defaultIfEmpty, map, finalize, filter } from 'rxjs/operators';
import { TprecmAuthorizationService } from '../../../authorization/tprecm-authorization.service';

const DEFAULT_MAX_VALUE = 1;

@Component({
    selector: 'tpr-build-progress',
    templateUrl: './tpr-build-progress.template.html',
    styleUrls: ['../../../tprecm.style.scss', './tpr-build-progress.scss']
})
export class TprBuildProgressComponent {

    disabled = false;
    _selectedTpr: ViewUpdateTpr;

    get selectedTpr(): ViewUpdateTpr {
        return this._selectedTpr;
    }

    @Input()
    set selectedTpr(value: ViewUpdateTpr) {
        this.disabled = false;
        if (value) {
            this._selectedTpr = value;
        } else {
            this.disabled = true;
            this._selectedTpr = new ViewUpdateTpr();
        }
    }

    @Input()
    reSourced = false;

    @Input()
    inActive = false;

    @Output()
    updateEvent = new EventEmitter<any>();

    daysOpts: any[] = Array(41).fill(0).map((_, index) => -20 + index);

    constructor(
        private readonly viewUpdateTprApiService: ViewUpdateTprApiService,
        private readonly toastr: ToastrService,
        private readonly auth: TprecmAuthorizationService
    ) { }

    inputRangeCheck(inputData: number, field: string) {
        if (!(inputData >= 0 && inputData <= 100)) {
            this.toastr.error(' Valid range is between 0 and 100 for ' + field);
            return false;
        }
        return true;
    }

    onFabPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.fabricationPerc, 'Fabrication Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.fabricationPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('fabrication', Math.round(this.selectedTpr.fabricationPerc) / 100, true);
    }

    onNcPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.ncPrgmPerc, 'NcProgram Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.ncPrgmPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('ncPrgm', Math.round(this.selectedTpr.ncPrgmPerc) / 100, true);
    }

    onMachinePercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.machiningPerc, 'Machining Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.machiningPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('machining', Math.round(this.selectedTpr.machiningPerc) / 100, true);
    }

    onAssemblyPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.assemblyPerc, 'On Assembly Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.assemblyPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('assembly', Math.round(this.selectedTpr.assemblyPerc) / 100, true);
    }

    onCertifyPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.certPerc, 'Certify Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.certPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('cert', Math.round(this.selectedTpr.certPerc) / 100, true);
    }

    onFinalPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.finalAsmPerc, 'Final Asm Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.finalAsmPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('finalAsm', Math.round(this.selectedTpr.finalAsmPerc) / 100, true);
    }

    onFabCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.fabricationPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('fabrication', DEFAULT_MAX_VALUE, true);
        }
    }

    onNcCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.ncPrgmPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('ncPrgm', DEFAULT_MAX_VALUE, true);
        }
    }

    onMachineCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.machiningPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('machining', DEFAULT_MAX_VALUE, true);
        }
    }

    onAssemblyCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.assemblyPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('assembly', DEFAULT_MAX_VALUE, true);
        }
    }

    onCertifyCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.certPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('cert', DEFAULT_MAX_VALUE, true);
        }
    }

    onFinalCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.finalAsmPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('finalAsm', DEFAULT_MAX_VALUE, true);
        }
    }

    onActualFinishChanged() {
        const start = new Date(this.selectedTpr.buildActualStart);
        start.setHours(0, 0, 0);

        if (this.selectedTpr.buildActualFinish && this.selectedTpr.buildActualFinish < start) {
            this.toastr.error('Date not saved. The Actual Date must be greater than or equal to the Actual Start date.');
            return;
        }

        this.onGenericFieldChange('buildActualFinish', this.selectedTpr.buildActualFinish || '', true);
    }

    fieldAuthorized(field: string) {
        return this.auth.getAuthorizationByField(field);
    }

    onGenericFieldChange(fieldName: string, fieldValue: any, calcFields = false) {

        this.disabled = true;
        const params = {
            toolId: this.selectedTpr.toolId,
            tprToolId: this.selectedTpr.tprToolId,
            calcFieldsRequired: calcFields,
            fieldName,
            fieldValue
        };
        this.viewUpdateTprApiService.updateGenericTprField(params)
            .pipe(
                filter(Boolean),
                defaultIfEmpty({ data: [], totalCount: 0 }),
                map((response: any) => response.data[0]),
                finalize(() => this.disabled = false)
            ).subscribe((updatedTpr: any) => this.updateEvent.emit(updatedTpr));
    }

    actualFinishEnabled() {
        return [
            this.selectedTpr.fabricationPerc,
            this.selectedTpr.ncPrgmPerc,
            this.selectedTpr.machiningPerc,
            this.selectedTpr.assemblyPerc,
            this.selectedTpr.certPerc,
            this.selectedTpr.finalAsmPerc
        ].every(val => {
            return val === 100;
        });
    }
}
